import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { C, FONT } from '../theme/tokens';

/**
 * The app's own ask-and-confirm box, replacing `window.prompt` /
 * `window.confirm`.
 *
 * Those are the browser's chrome, not this app's: they render in the OS's
 * font at the top of the window, ignore the theme entirely, and read as
 * "something went wrong with the website" rather than as part of the
 * console. One component covers both shapes — pass `defaultValue` for a text
 * prompt, omit it for a plain confirm.
 */
const AskDialog: React.FC<{
  open: boolean;
  title: string;
  /** Optional explanation under the title — say what the action will do. */
  body?: string;
  /** Present = text prompt; absent = confirm. */
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  /** Confirms in red, for anything that destroys data. */
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: (value: string) => void;
}> = ({ open, title, body, defaultValue, placeholder, confirmLabel = 'OK', destructive, onCancel, onConfirm }) => {
  const [value, setValue] = useState(defaultValue ?? '');

  // Re-seed each time it opens: the same dialog instance is reused for
  // different rows, and a stale value from the last one would be worse than
  // an empty field.
  useEffect(() => {
    if (open) setValue(defaultValue ?? '');
  }, [open, defaultValue]);

  const isPrompt = defaultValue !== undefined;
  const submit = () => {
    if (isPrompt && !value.trim()) return;
    onConfirm(value.trim());
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontFamily: FONT.display, fontWeight: 700, fontSize: '1rem', color: C.text }}>{title}</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Stack spacing={1.5}>
          {body && <Typography sx={{ color: C.textDim, fontSize: '0.84rem' }}>{body}</Typography>}
          {isPrompt && (
            <TextField
              autoFocus
              size="small"
              value={value}
              placeholder={placeholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onCancel} sx={{ color: C.textDim }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color={destructive ? 'error' : 'secondary'}
          disabled={isPrompt && !value.trim()}
          onClick={submit}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AskDialog;
